/**
 * 👗 HeroWardrobe — owner-driven outfit swap panel.
 *
 * Replaces the old Emergent hero-preview pedestal that was doing double
 * duty on the Hero Window. Owner has his own portrait + HeroMount for
 * the hero sprite; this slot is now a wardrobe for CHANGING CLOTHES.
 *
 * Outfits are OWNER-PROVIDED. Do NOT invent outfits, art, or names —
 * populate `OUTFITS` as the owner delivers each one. Empty state is
 * intentional until the owner drops the first outfit here.
 */
import { useMemo, useState } from "react";

// ─────────────────────── Outfit catalog ───────────────────────
// 🖼️ Owner will add outfits here one at a time. Each entry:
//   {
//     id:        "unique_slug",
//     name:      "Display name",
//     subtitle:  "optional subtitle / set tag",
//     art_url:   "https://.../outfit.webp",
//     by_hero?:  ["bram", "xyla", ...]  // null = universal
//   }
export const OUTFITS = [];

export default function HeroWardrobe({ heroName, className }) {
  const available = useMemo(() => {
    const n = String(heroName || "").trim().toLowerCase().split(/[\s,]+/)[0];
    const c = String(className || "").trim().toLowerCase().split(/[\s,]+/)[0];
    return OUTFITS.filter((o) => {
      if (!o.by_hero || !o.by_hero.length) return true;
      return o.by_hero.includes(n) || o.by_hero.includes(c);
    });
  }, [heroName, className]);

  const [activeIdx, setActiveIdx] = useState(0);
  const active = available[activeIdx] || null;

  const prev = () => setActiveIdx((i) => (i - 1 + available.length) % available.length);
  const next = () => setActiveIdx((i) => (i + 1) % available.length);

  return (
    <div
      className="w-full flex flex-col items-center"
      data-testid="hero-wardrobe"
    >
      {/* Header */}
      <div className="w-full flex items-center justify-between px-1 pb-2">
        <span className="text-[10px] uppercase tracking-widest text-amber-300/80">
          Wardrobe
        </span>
        {available.length > 0 && (
          <span className="text-[9px] uppercase tracking-widest text-amber-400/60 font-mono">
            {activeIdx + 1} / {available.length}
          </span>
        )}
      </div>

      {/* Wardrobe panel */}
      <div
        className="aspect-[3/4] w-full rounded-sm border-2 border-amber-700/40
                   bg-gradient-to-b from-[#1b0f21] via-[#140a18] to-[#0b0510]
                   relative overflow-hidden"
        data-testid="hero-wardrobe-panel"
      >
        {/* Subtle wardrobe rail (visual-only — gives the panel a
            "closet" feel without inventing content). */}
        <div
          className="absolute inset-x-6 top-5 h-1 rounded-full
                     bg-gradient-to-r from-amber-900/0 via-amber-500/40 to-amber-900/0"
          aria-hidden="true"
        />
        <div
          className="absolute inset-x-6 top-5 flex items-start justify-between pointer-events-none"
          aria-hidden="true"
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="w-[2px] h-6 bg-amber-700/50 translate-y-[2px]"
            />
          ))}
        </div>

        {/* Empty state — owner hasn't dropped outfits yet */}
        {!active && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center text-center px-6"
            data-testid="hero-wardrobe-empty"
          >
            <div className="text-4xl mb-3 opacity-70" aria-hidden="true">👗</div>
            <div className="text-amber-300 text-sm font-bold uppercase tracking-widest">
              No outfits yet
            </div>
            <div className="text-amber-200/60 text-[11px] mt-2 leading-snug">
              Outfits will appear here as the owner adds them.
            </div>
          </div>
        )}

        {/* Active outfit render */}
        {active && (
          <div
            className="absolute inset-0 flex items-end justify-center pb-4"
            data-testid="hero-wardrobe-outfit"
          >
            <img
              src={active.art_url}
              alt={active.name}
              className="max-h-[85%] max-w-[85%] object-contain drop-shadow-lg"
              data-testid="hero-wardrobe-outfit-art"
            />
          </div>
        )}

        {/* Prev / Next controls — only show when 2+ outfits available */}
        {available.length > 1 && (
          <>
            <button
              onClick={prev}
              aria-label="Previous outfit"
              data-testid="hero-wardrobe-prev"
              className="absolute left-1 top-1/2 -translate-y-1/2
                         w-7 h-7 flex items-center justify-center
                         text-amber-300 text-lg font-bold rounded-sm
                         bg-black/50 border border-amber-700/50
                         hover:border-amber-400 hover:text-amber-100 transition"
            >
              ‹
            </button>
            <button
              onClick={next}
              aria-label="Next outfit"
              data-testid="hero-wardrobe-next"
              className="absolute right-1 top-1/2 -translate-y-1/2
                         w-7 h-7 flex items-center justify-center
                         text-amber-300 text-lg font-bold rounded-sm
                         bg-black/50 border border-amber-700/50
                         hover:border-amber-400 hover:text-amber-100 transition"
            >
              ›
            </button>
          </>
        )}
      </div>

      {/* Outfit name + subtitle */}
      {active && (
        <div
          className="w-full mt-2 text-center"
          data-testid="hero-wardrobe-outfit-meta"
        >
          <div className="text-amber-200 text-xs font-bold uppercase tracking-widest">
            {active.name}
          </div>
          {active.subtitle && (
            <div className="text-amber-300/60 text-[10px] tracking-wider mt-0.5">
              {active.subtitle}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
